// Copyright 2019 Google LLC
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
/** @module common-sk/modules/human
 *  @description Utitities for working with human friendly I/O.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: all[name]
    });
}
_export(exports, {
    KB: function() {
        return KB;
    },
    MB: function() {
        return MB;
    },
    GB: function() {
        return GB;
    },
    TB: function() {
        return TB;
    },
    PB: function() {
        return PB;
    },
    pad: function() {
        return pad;
    },
    strDuration: function() {
        return strDuration;
    },
    diffDate: function() {
        return diffDate;
    },
    bytes: function() {
        return bytes;
    },
    localeTime: function() {
        return localeTime;
    }
});
const TIME_DELTAS = [
    {
        units: 'w',
        delta: 7 * 24 * 60 * 60
    },
    {
        units: 'd',
        delta: 24 * 60 * 60
    },
    {
        units: 'h',
        delta: 60 * 60
    },
    {
        units: 'm',
        delta: 60
    },
    {
        units: 's',
        delta: 1
    }
];
const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;
const TB = GB * 1024;
const PB = TB * 1024;
const BYTES_DELTAS = [
    {
        units: ' PB',
        delta: PB
    },
    {
        units: ' TB',
        delta: TB
    },
    {
        units: ' GB',
        delta: GB
    },
    {
        units: ' MB',
        delta: MB
    },
    {
        units: ' KB',
        delta: KB
    },
    {
        units: ' B',
        delta: 1
    }
];
function pad(num, size) {
    let str = `${num}`;
    while(str.length < size){
        str = `0${str}`;
    }
    return str;
}
function strDuration(seconds) {
    if (seconds < 0) {
        seconds = -seconds;
    }
    if (seconds === 0) {
        return '  0s';
    }
    let rv = '';
    for (const td of TIME_DELTAS){
        if (td.delta <= seconds) {
            let s = Math.floor(seconds / td.delta) + td.units;
            while(s.length < 4){
                s = ` ${s}`;
            }
            rv += s;
            seconds %= td.delta;
        }
    }
    return rv;
}
function diffDate(s, now) {
    if (now === undefined) {
        now = Date.now();
    }
    const ms = typeof s === 'number' ? s : Date.parse(s);
    let diff = (ms - now) / 1000;
    if (diff < 0) {
        // eslint-disable-next-line operator-assignment
        diff = -1.0 * diff;
    }
    return humanize(diff, TIME_DELTAS);
}
function bytes(b, unit = 1) {
    return humanize(b * unit, BYTES_DELTAS);
}
function localeTime(date) {
    // caching timezone could be buggy, especially if times from a wide range
    // of dates are used. The main concern would be crossing over Daylight
    // Savings time and having some times be erroneously in EST instead of
    // EDT, for example
    const str = date.toString();
    const timezone = str.substring(str.indexOf('('));
    return `${date.toLocaleString()} ${timezone}`;
}
function humanize(n, deltas) {
    for(let i = 0; i < deltas.length - 1; i++){
        // If n would round to '60s', return '1m' instead.
        const nextDeltaRounded = Math.round(n / deltas[i + 1].delta) * deltas[i + 1].delta;
        if (nextDeltaRounded / deltas[i].delta >= 1) {
            return Math.round(n / deltas[i].delta) + deltas[i].units;
        }
    }
    const index = deltas.length - 1;
    return Math.round(n / deltas[index].delta) + deltas[index].units;
}
